import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import Hero from './components/Hero';
import HomeShowcase from './components/HomeShowcase';
import Services from './components/Services';
import About from './components/About';
import Portfolio from './components/Portfolio';
import Team from './components/Team';
import Careers from './components/Careers';
import Contact from './components/Contact';
import Footer from './components/Footer';
import AttendanceSystem from './components/AttendanceSystem';
import AdminDashboard from './components/AdminDashboard';
import PendingApprovals from './components/PendingApprovals';
import WhatsAppWidget from './components/WhatsAppWidget';
import EmployeeProfile from './components/EmployeeProfile';
import TeamsApp from "../teams/src/App";
import JobPostings from '../HR/pages/JobPostings';
import HRLayout from '../HR/layout/HRLayout';
import HRDashboard from '../HR/pages/HRDashboard';
import Employees from '../HR/pages/Employees';
import LeaveManagement from '../HR/pages/LeaveManagement';
import Payroll from '../HR/pages/Payroll';


// JIRA
import JiraDashboard from './components/JiraDashboard/JiraDashboard';
import Projects from './components/JiraDashboard/Projects';
import Board from './components/JiraDashboard/Board';
import Backlog from './components/JiraDashboard/Backlog';
import Issues from './components/JiraDashboard/Issues';
import TimeTrackingPage from './components/JiraDashboard/Time Tracking';
import ReportsPage from './components/JiraDashboard/Report';
import TeamManagementPage from './components/JiraDashboard/Team Management';
import SettingsPage from './components/JiraDashboard/SettingsPage';

// CLIENT
import ClientDashboard from './components/Client/Client1';
import ClientProgressPage from './components/Client/ClientProgress';
import ClientMyProject from './components/Client/myproject';
import ClientTeam from './components/Client/ProjectTeam';
import ClientDocuments from './components/Client/Documents';

// ROLE TAILORED DASHBOARDS
import SalesDashboard from './components/Sales/SalesDashboard';
import MarketingDashboard from './components/Marketing/MarketingDashboard';
import EmployeeDashboard from './components/Employee/EmployeeDashboard';
import HRDashboardView from './components/HR/HRDashboardView';

const AppContent: React.FC = () => {

  const [currentView, setCurrentView] = useState<
    | 'home'
    | 'attendance'
    | 'admin'
    | 'pending'
    | 'jira'
    | 'projects'
    | 'board'
    | 'backlog'
    | 'issues'
    | 'time'
    | 'reports'
    | 'team'
    | 'settings'
    | 'profile'
    | 'dashboard'
    | 'sales-dashboard'
    | 'marketing-dashboard'
    | 'employee-dashboard'
    | 'client-progress'
    | 'client-myproject'
    | 'client-team'
    | 'client-documents'
    | 'teams'
    | 'hr-dashboard'
    | 'jobs'
    | 'employees'
    | 'leave'
    | 'payroll'
    | 'services'
    | 'about'
    | 'portfolio'
    | 'careers'
    | 'contact'
  >('home');

  const { user, isAuthenticated } = useAuth();

  // ROLE CHECK
  const isAdmin = user?.role === 'admin';
  const isHR = user?.role === 'hr';
  const isEmployee = user?.role === 'employee';
  const isPM = user?.role === 'product_manager';
  const isClient = user?.role === 'client';
  const isScrumMaster = user?.role === 'scrum_master';
  const isFinance = user?.role === 'finance';
  const isSales = user?.role === 'sales' || (user?.department || '').toLowerCase() === 'sales';
  const isMarketing = user?.role === 'marketing' || (user?.department || '').toLowerCase() === 'marketing';


  // ACCESS CONTROL
  const canAccessJira =
    isAuthenticated && (isAdmin || isHR || isEmployee || isPM || isScrumMaster || isClient);

  const canAccessClientDashboard =
    isAuthenticated && (isAdmin || isHR || isClient || isEmployee || isScrumMaster || isClient);

  // HASH ROUTING WITH ROLE SECURITY
  useEffect(() => {

    const handleHashChange = () => {

      const hash = window.location.hash.replace('#', '');

      console.log("HASH:", hash);
      console.log("ROLE:", user?.role);

      // DEDICATED ROLE DASHBOARDS
      if (hash === 'sales-dashboard') {
        setCurrentView(isAuthenticated && (isAdmin || isSales) ? 'sales-dashboard' : 'home');
      } else if (hash === 'marketing-dashboard') {
        setCurrentView(isAuthenticated && (isAdmin || isMarketing) ? 'marketing-dashboard' : 'home');
      } else if (hash === 'employee-dashboard') {
        setCurrentView(isAuthenticated ? 'employee-dashboard' : 'home');
      } else if (hash === 'hr-dashboard') {
        setCurrentView(isAdmin || isHR ? 'hr-dashboard' : 'home');
      }

      // GENERIC DASHBOARD (Routes directly to user's dedicated dashboard)
      else if (hash === 'dashboard') {
        if (!isAuthenticated) {
          setCurrentView('home');
        } else if (isClient) {
          setCurrentView('dashboard');
        } else if (isHR) {
          setCurrentView('hr-dashboard');
        } else if (isSales) {
          setCurrentView('sales-dashboard');
        } else if (isMarketing) {
          setCurrentView('marketing-dashboard');
        } else if (isAdmin) {
          setCurrentView('admin');
        } else {
          setCurrentView('employee-dashboard');
        }
      }

      // ADMIN / BILLING ACCESS
      else if (hash === 'admin' || hash === 'billing') {
        if (!isAuthenticated) {
          setCurrentView('home');
        } else if (isAdmin || isFinance) {
          setCurrentView('admin');
        } else if (isHR) {
          setCurrentView('hr-dashboard');
        } else if (isSales) {
          setCurrentView('sales-dashboard');
        } else if (isMarketing) {
          setCurrentView('marketing-dashboard');
        } else {
          setCurrentView('employee-dashboard');
        }
      }

      else if (hash === 'pending')
        setCurrentView(isAdmin || isHR ? 'pending' : 'home');

      // EMPLOYEE / PM / ADMIN
      else if (hash === 'attendance')
        setCurrentView(isAuthenticated ? 'attendance' : 'home');

      else if (hash === 'profile')
        setCurrentView(isAuthenticated ? 'profile' : 'home');


      // JIRA ACCESS
      else if (hash === 'jira')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'jira') : 'home');

      else if (hash === 'projects')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'projects') : 'home');
      else if (hash === 'board')
        setCurrentView(
          isAuthenticated && canAccessJira
            ? 'board'
            : 'home'
        );

      else if (hash === 'backlog')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'backlog') : 'home');

      else if (hash === 'issues')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'issues') : 'home');

      else if (hash === 'time')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'time') : 'home');

      else if (hash === 'reports')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'reports') : 'home');

      else if (hash === 'team')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'team') : 'home');

      else if (hash === 'settings')
        setCurrentView(canAccessJira ? (isEmployee || isClient ? 'board' : 'settings') : 'home');


      // CLIENT DASHBOARD ACCESS
      else if (hash === 'dashboard')
        setCurrentView(canAccessClientDashboard ? 'dashboard' : 'home');

      // HR MODULE
else if (hash === 'hr-dashboard')
  setCurrentView(isAdmin || isHR ? 'hr-dashboard' : 'home');

else if (hash === 'employees')
  setCurrentView(isAdmin || isHR ? 'employees' : 'home');

else if (hash === 'jobs')   // ✅ ADD THIS
  setCurrentView(isAdmin || isHR ? 'jobs' : 'home');

else if (hash === 'leave')
  setCurrentView(isAuthenticated ? 'leave' : 'home');

else if (hash === 'payroll')
  setCurrentView(isAdmin || isHR ? 'payroll' : 'home');

      else if (hash === 'client-progress')
        setCurrentView(canAccessClientDashboard ? 'client-progress' : 'home');

      else if (hash === 'client-myproject')
        setCurrentView(canAccessClientDashboard ? 'client-myproject' : 'home');

      else if (hash === 'client-team')
        setCurrentView(canAccessClientDashboard ? 'client-team' : 'home');

      else if (hash === 'client-documents')
        setCurrentView(canAccessClientDashboard ? 'client-documents' : 'home');

      else if (hash === 'teams')
        setCurrentView('teams');
      else if (hash === 'services')
        setCurrentView('services');
      else if (hash === 'about')
        setCurrentView('about');
      else if (hash === 'portfolio')
        setCurrentView('portfolio');
      else if (hash === 'careers')
        setCurrentView('careers');
      else if (hash === 'contact')
        setCurrentView('contact');
      else
        setCurrentView('home');

    };

    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);

    return () =>
      window.removeEventListener('hashchange', handleHashChange);

  }, [user, isAuthenticated]);


  // BODY RESET
  useEffect(() => {

    document.body.style.pointerEvents = "auto";
    document.body.style.overflow = "auto";

  }, [currentView]);




  return (

    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-grow">


        {currentView === 'attendance' && <div className="pt-20"><AttendanceSystem /></div>}

        {currentView === 'admin' && <div className="pt-20"><AdminDashboard /></div>}

        {currentView === 'pending' && <div className="pt-20"><PendingApprovals /></div>}

        {currentView === 'profile' && <div className="pt-20"><EmployeeProfile /></div>}


        {/* CLIENT */}
        {currentView === 'dashboard' && <div className="pt-20"><ClientDashboard /></div>}

        {currentView === 'client-progress' && <div className="pt-20"><ClientProgressPage /></div>}

        {currentView === 'client-myproject' && <div className="pt-20"><ClientMyProject /></div>}

        {currentView === 'client-team' && <div className="pt-20"><ClientTeam /></div>}

        {currentView === 'client-documents' && <div className="pt-20"><ClientDocuments /></div>}


        {/* JIRA */}
        {currentView === 'jira' && <div className="pt-20"><JiraDashboard /></div>}

        {currentView === 'projects' && <div className="pt-20"><Projects /></div>}

        {currentView === 'board' && <div className="pt-20"><Board /></div>}

        {currentView === 'backlog' && <div className="pt-20"><Backlog /></div>}

        {currentView === 'issues' && <div className="pt-20"><Issues /></div>}

        {currentView === 'time' && <div className="pt-20"><TimeTrackingPage /></div>}

        {currentView === 'reports' && <div className="pt-20"><ReportsPage /></div>}

        {currentView === 'team' && <div className="pt-20"><TeamManagementPage /></div>}

        {currentView === 'settings' && <div className="pt-20"><SettingsPage /></div>}
        {currentView === 'teams' && (
        <div style={{ marginTop: "80px", height: "calc(100vh - 80px)" }}>
        <TeamsApp />
        </div>
        )}

        {/* ROLE TAILORED DASHBOARDS */}
        {currentView === 'sales-dashboard' && <div className="pt-20"><SalesDashboard /></div>}
        {currentView === 'marketing-dashboard' && <div className="pt-20"><MarketingDashboard /></div>}
        {currentView === 'employee-dashboard' && <div className="pt-20"><EmployeeDashboard /></div>}
        {currentView === 'hr-dashboard' && <div className="pt-20"><HRDashboardView /></div>}

        {currentView === 'employees' && (
          <HRLayout>
            <Employees />
          </HRLayout>
        )}

{currentView === 'jobs' && (
  <HRLayout>
    <JobPostings />
  </HRLayout>
)}

{currentView === 'leave' && (
  <HRLayout>
    <LeaveManagement />
  </HRLayout>
)}

{currentView === 'payroll' && (
  <HRLayout>
    <Payroll />
  </HRLayout>
)}
        {/* PAGES */}
        {currentView === 'services' && <div className="pt-20"><Services /></div>}
        {currentView === 'about' && <div className="pt-20"><About /></div>}
        {currentView === 'portfolio' && <div className="pt-20"><Portfolio /></div>}
        {currentView === 'careers' && <div className="pt-20"><Careers /></div>}
        {currentView === 'contact' && <div className="pt-20"><Contact /></div>}

        {/* HOME */}
        {currentView === 'home' && (
          <>
            <Hero />
            <HomeShowcase />
          </>
        )}
      </main>
      {currentView !== 'teams' && (
        <>
          <Footer />
          <WhatsAppWidget />
        </>
      )}

    </div>

  );

};


export default function App() {

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );

}
